import { FormControl, MenuItem, Select, SelectChangeEvent } from "@mui/material";
import { GridRenderCellParams } from "@mui/x-data-grid-pro";
import { cloneDeep } from "lodash";
import { useEffect, useState } from "react";

import { ISpreadsheetGrid } from "../../../../components/Spreadsheet/ISpreadsheetGrid";
import { DataGridColDef } from "../../../../utils/types/listItems";
import { InvestorCommitment, InvestorRow, Workbook } from "../../../../utils/types/workbook.type";
import { InvestorCommitmentRowType } from "../AllocationWorkbook.types";


enum ChangeType { Added, Removed, RenamedFrom }
type GridRow = { id: string, name: string, changeType: ChangeType, renamedFrom: string}; 

const investorNameType = 'A';
const investorNameCol = 'B';

export const useInvestorIdReconcilerEffect = (workbook: Workbook, investorCommitments: InvestorCommitment[], grid: ISpreadsheetGrid, onSave: () => void) => {
    const [gridRows, setGridRows] = useState<GridRow[]>([]);
    const [hasPossibleInvestorNameChanges, setHasPossibleInvestorNameChanges] = useState(false);

    useEffect(() => {
        workbook.investorRows = getInvestorNamesFromWB();

        const newGridRows = getPossibleNewInvestors(workbook.investorRows);
        const removedRows = getPossibleRemovedInvestors(workbook.investorRows);

        if(newGridRows.length === 0 || removedRows.length === 0) {
            setHasPossibleInvestorNameChanges(false);
            onSave();
            return;
        }

        const tmpGridRows = [...newGridRows, ...removedRows];

        setGridRows( tmpGridRows );
        setHasPossibleInvestorNameChanges(true);
    }, []);


    function getInvestorNamesFromWB() {
        const investorRows: InvestorRow[] = [];

        let currInvestorNameVal = '';
        
        // eslint-disable-next-line no-constant-condition
        for(let currRow = 5; true; currRow++) {
            currInvestorNameVal = String(grid.getCellValue(currRow, investorNameCol));

            if(currInvestorNameVal === '') continue;

            const prevInvestorNameVal = String(grid.getCellValue(currRow-1, investorNameCol));
            const nextInvestorNameVal = String(grid.getCellValue(currRow+1, investorNameCol));

            if(currInvestorNameVal.startsWith('Total') && prevInvestorNameVal === '' && nextInvestorNameVal === '') {
                const totalTyoe = currInvestorNameVal.split(' ')[1];

                investorRows.push({
                    investorId: '-',
                    investorName: '-',
                    investorType:  totalTyoe,
                    rowType: totalTyoe === "Partners" ? InvestorCommitmentRowType.PartnerTotal : InvestorCommitmentRowType.TypeTotal,
                    row: currRow,
                    deleted: false
                });

                if(totalTyoe === "Partners") break;

            } else {
                const currInvestorType = String(grid.getCellValue(currRow, investorNameType));
                const investorCommitment = investorCommitments.find(ic => ic.investorName === currInvestorNameVal);

                let investorId: string;

                if(investorCommitment) {
                    investorId = investorCommitment.investorId;
                } else {
                    investorId = `V1 Delete: ${new Date().getTime()}`;
                }

                investorRows.push({
                    investorId: investorId,
                    investorName: currInvestorNameVal,
                    investorType: currInvestorType,
                    rowType: InvestorCommitmentRowType.Investor,
                    row: currRow,
                    deleted: !investorCommitment
                });
            }
        } 

        return investorRows;
    }

    function getPossibleNewInvestors(wbInvestorRows: InvestorRow[]) {
        const gridRows: GridRow[] = [];

        investorCommitments.forEach(ic => {
            const wbInvestorRow = wbInvestorRows.find(wbi => wbi.investorId === ic.investorId);

            if(!wbInvestorRow) {
                gridRows.push({
                    id: ic.investorId,
                    name: ic.investorName,
                    changeType: ChangeType.Added,
                    renamedFrom: ''
                });
            }
        });

        return gridRows;
    }

    function getPossibleRemovedInvestors(wbInvestorRows: InvestorRow[]) {

        const gridRows = wbInvestorRows.filter(wbi => wbi.deleted).map((wbi, index) => {
            return {
                id: wbi.investorName,
                name: wbi.investorName,
                changeType: ChangeType.Removed,
                renamedFrom: ''
            } as GridRow;
        });

        return gridRows;
    }




    const headerList:DataGridColDef[] = 
    [
      {
        field: "name",
        headerName: "Investor",
        hide: false,
        index: 1,
        type: "string",
        sortable: false,
        align: "left",
        width: 80,
        disableReorder: true
      },
      {
        field: "changeType",
        headerName: "Change",
        hide: false,
        index: 2,
        type: "string",
        sortable: false,
        align: "left",
        width: 250,
        disableReorder: true,
        renderCell: dataGridChangeTypeSelect
      },
      {
        field: "renamedFrom",
        headerName: "Renamed From",
        hide: false,
        index: 2,
        type: "string",
        sortable: false,
        align: "left",
        width: 250,
        disableReorder: true,
        renderCell: dataGridChangeRenamdFromSelect
      }
    ];

    function dataGridChangeTypeSelect(params: GridRenderCellParams<any, any, any>) {
        const gridRow = params.row as GridRow;

        if (gridRow.changeType === ChangeType.Removed) {
            return 'Removed';
        }

        return (
            <FormControl sx={{
                minWidth: '100%'
            }}>
                <Select
                    id={`investor_row_${gridRow.id}`}
                    labelId={`investor_row_${gridRow.id}_label`}
                    label="Investor"
                    value={gridRow.changeType}
                    onChange={e => handleRowTypeChange(gridRow, e)}
                    MenuProps={{
                        PaperProps: {
                            sx: {
                                padding: '10px',
                                height: '100px',
                                width: '180px',
                                '& .MuiBox-root': { width: '100%' },
                                '& MuiFormControl-root': {  minWidth: '100%' }
                            }
                        }
                    }}
                >
                    <MenuItem 
                        sx={ { minWidth: '100%' } } 
                        key={ ChangeType.Added } 
                        value={ ChangeType.Added }
                    >
                        New
                    </MenuItem>
                    <MenuItem 
                        sx={ { minWidth: '100%' } } 
                        key={ ChangeType.RenamedFrom } 
                        value={ ChangeType.RenamedFrom }
                    >
                        Rename
                    </MenuItem>
                </Select>
            </FormControl>
        );

        function handleRowTypeChange(gridRow: GridRow, e: SelectChangeEvent<ChangeType.Added | ChangeType.RenamedFrom>) {
            const oldGridRow = gridRows.find(gr => gr.id === gridRow.id)!;
            const changeType = e.target.value;

            oldGridRow.changeType = changeType as ChangeType;

            if(oldGridRow.changeType === ChangeType.Added) {
                oldGridRow.renamedFrom = '';
            }

            setGridRows( cloneDeep(gridRows) );
        }
    }



    function dataGridChangeRenamdFromSelect(params: GridRenderCellParams<any, any, any>) {
        const gridRow = params.row as GridRow;
        const removedGridRows = gridRows.filter(gr => gr.changeType === ChangeType.Removed);
        const existingRenamed = gridRows.filter(gr => gr.renamedFrom !== gridRow.renamedFrom && !!gr.renamedFrom);
        const renamedChoices = removedGridRows.filter(gr => !existingRenamed.find(er => er.renamedFrom === gr.id));

        if(gridRow.changeType !== ChangeType.RenamedFrom)
            return null;

        return (
            <FormControl sx={{
                minWidth: '100%'
            }}>
                <Select
                    id={`investor_row_${gridRow.id}`}
                    labelId={`investor_row_${gridRow.id}_label`}
                    label="Investor"
                    value={gridRow.renamedFrom}
                    onChange={e => handleRowRenamedFromChange(gridRow, e)}
                    MenuProps={{
                        PaperProps: {
                            sx: {
                                padding: '10px',
                                height: '200px',
                                width: '180px',
                                '& .MuiBox-root': { width: '100%' },
                                '& MuiFormControl-root': {  minWidth: '100%' }
                            }
                        }
                    }}
                >
                    {
                        getMenuItems(gridRow)
                    }
                </Select>
            </FormControl>
        );

        function getMenuItems(gridRow: GridRow) {

            return renamedChoices.map(rgr => {
                return (
                    <MenuItem 
                        sx={ { minWidth: '100%' } } 
                        key={ rgr.id } 
                        value={ rgr.id }
                    >
                        { rgr.name }
                    </MenuItem>
                );
            });
        }

        function handleRowRenamedFromChange(gridRow: GridRow, e: any) {
            const oldGridRow = gridRows.find(gr => gr.id === gridRow.id)!;
            const renamedFromId = e.target.value as string;

            oldGridRow.renamedFrom = renamedFromId;

            setGridRows( cloneDeep(gridRows) );
        }
    }




    function getGridRows() {
        const renamedFromIds = gridRows.filter(gr => !!gr.renamedFrom).map(gr => gr.renamedFrom);
        const filteredGridRows = gridRows.filter(gr => !renamedFromIds.find(id => id === gr.id));

        return filteredGridRows;
    }

    function isFormValid() {
        const invalidGridRows = gridRows.filter(gr => gr.changeType === ChangeType.RenamedFrom && !gr.renamedFrom);

        return !invalidGridRows || invalidGridRows.length === 0;
    }


    function handleSave() {
        const renamedGridRows = gridRows.filter(gr => gr.changeType === ChangeType.RenamedFrom);

        renamedGridRows.forEach(rgr => {
            const wbInvestor = workbook.investorRows.find(ir => ir.investorName)!;

            wbInvestor.investorId = rgr.id;
            wbInvestor.investorName = rgr.name;
            wbInvestor.deleted = false;
        });


        setHasPossibleInvestorNameChanges(false);
        onSave();
    }

    return {
        hasInvestorNameChanges: hasPossibleInvestorNameChanges,
        headerList,
        getGridRows,
        isFormValid,
        handleSave
    };    
};